import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  FileText, Download, Loader2, Receipt, CheckCircle, 
  Calendar, DollarSign, Ticket, Eye, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicesList = ({ token }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (invoice) => {
    setViewingInvoice(invoice.transaction_id);
    setLoadingDetails(true);
    try {
      const response = await axios.get(
        `${API}/invoices/${invoice.transaction_id}/view`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoiceDetails(response.data);
    } catch (error) {
      console.error('View error:', error);
      toast.error('Erreur lors du chargement de la facture');
      setViewingInvoice(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDownload = async (invoice) => {
    setDownloading(invoice.transaction_id);
    try {
      const response = await axios.get(
        `${API}/invoices/${invoice.transaction_id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoice.reference}_${invoice.party}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Facture téléchargée');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(null);
    }
  };

  const formatCurrency = (amount, currency = 'CAD') => {
    return new Intl.NumberFormat('fr-CA', { 
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('fr-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  // Invoice detail modal
  const InvoiceModal = () => {
    if (!viewingInvoice || !invoiceDetails) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-900">
              {invoiceDetails.party === 'buyer' ? 'Facture' : 'Reçu'} {invoiceDetails.invoice_number}
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setViewingInvoice(null); setInvoiceDetails(null); }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="text-center pb-4 border-b">
              <h2 className="text-2xl font-bold text-brand-800">MIDDLEMAN</h2>
              <p className="text-sm text-gray-500">Transferts de billets sécurisés</p>
            </div>
            
            {/* Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Date:</span>
                <p className="font-medium">{formatDate(invoiceDetails.date)}</p>
              </div>
              <div>
                <span className="text-gray-500">Référence:</span>
                <p className="font-medium">{invoiceDetails.reference}</p>
              </div>
              <div>
                <span className="text-gray-500">Client:</span>
                <p className="font-medium">{invoiceDetails.client?.name || invoiceDetails.client?.email}</p>
              </div>
              <div>
                <span className="text-gray-500">N° Client:</span>
                <p className="font-medium">{invoiceDetails.client?.client_number || 'N/A'}</p>
              </div>
            </div>
            
            {/* Event details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Détails de la transaction</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Événement:</span>
                  <span className="font-medium">{invoiceDetails.event?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date de l'événement:</span>
                  <span className="font-medium">{formatDate(invoiceDetails.event?.date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Billets:</span>
                  <span className="font-medium">{invoiceDetails.event?.ticket_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Votre rôle:</span>
                  <span className="font-medium">{invoiceDetails.party_label}</span>
                </div>
              </div>
            </div>
            
            {/* Fee breakdown */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-brand-800 text-white p-3 flex justify-between text-sm font-semibold">
                <span>Description</span>
                <span>Montant</span>
              </div>
              <div className="p-3 flex justify-between text-sm border-b">
                <span>Transfert de billets sécurisé</span>
                <span className="font-medium">{formatCurrency(invoiceDetails.pricing?.transfer_fee, invoiceDetails.pricing?.currency)}</span>
              </div>
              <div className="bg-green-600 text-white p-3 flex justify-between font-bold">
                <span>TOTAL</span>
                <span>{formatCurrency(invoiceDetails.pricing?.total, invoiceDetails.pricing?.currency)}</span>
              </div>
            </div>
            
            {/* Status */}
            <div className="text-center">
              <span className="inline-flex items-center gap-2 text-green-600 font-bold text-lg">
                <CheckCircle className="h-5 w-5" />
                PAYÉ
              </span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="sticky bottom-0 bg-gray-50 border-t p-4 flex justify-end gap-3">
            <Button 
              variant="outline"
              onClick={() => { setViewingInvoice(null); setInvoiceDetails(null); }}
            >
              Fermer
            </Button>
            <Button 
              onClick={() => {
                const invoice = invoices.find(i => i.transaction_id === viewingInvoice);
                if (invoice) handleDownload(invoice);
              }}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Télécharger PDF
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-brand-600" />
            Mes factures et reçus
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Documents disponibles pour vos transferts terminés
          </p>
        </div>
      </div>

      {invoices.length > 0 ? (
        <div className="grid gap-4">
          {invoices.map((invoice, index) => (
            <Card key={`${invoice.invoice_number}-${invoice.party}-${index}`} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${invoice.type === 'facture' ? 'bg-blue-100' : 'bg-green-100'}`}>
                      {invoice.type === 'facture' ? (
                        <FileText className="h-6 w-6 text-blue-600" />
                      ) : (
                        <Receipt className="h-6 w-6 text-green-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-gray-900">
                          {invoice.invoice_number}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          invoice.type === 'facture' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {invoice.type === 'facture' ? 'Facture' : 'Reçu'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 font-medium mb-1">
                        <Ticket className="h-3.5 w-3.5 inline mr-1" />
                        {invoice.event_name || 'Événement'}
                      </p>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(invoice.date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          Frais de transfert: {formatCurrency(invoice.amount, invoice.currency)}
                        </span>
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          Payé
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </p>
                      <p className="text-xs text-gray-500">Frais</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleView(invoice)}
                        disabled={loadingDetails && viewingInvoice === invoice.transaction_id}
                        variant="outline"
                        className="gap-2"
                        size="sm"
                      >
                        {loadingDetails && viewingInvoice === invoice.transaction_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                        Voir
                      </Button>
                      <Button
                        onClick={() => handleDownload(invoice)}
                        disabled={downloading === invoice.transaction_id}
                        className="gap-2"
                        size="sm"
                      >
                        {downloading === invoice.transaction_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        PDF
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucune facture disponible
              </h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Les factures et reçus seront disponibles ici une fois que vos transferts seront terminés.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      
      <InvoiceModal />
    </div>
  );
};

export default InvoicesList;
